import { useEffect, useState } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  acoesDoEstatuto,
  buildEstatutoPublicUrl,
  downloadBlob,
  IEditarEstatutoData,
  useEstatuto,
} from '../../../../hooks/useEstatuto';
import { useKanban } from '../../../../hooks/useKanban';
import { useDialog } from '../../../../providers/dialog';
import { copyToClipboard, STATUS_COLORS } from '../utils';

interface UseEstatutoCardActionsParams {
  isOpen: boolean;
  estatutoId: number | null;
  onClose: () => void;
  canFinalizar?: boolean;
  onFinalizar?: () => void;
  finalizando?: boolean;
}

/**
 * Hook que concentra TODO o estado/lógica das ações de um estatuto (portado
 * fielmente do antigo EstatutoDrawer): query do estatuto, checkbox "aguardando
 * cliente" + motivo, disclosures de assinatura/pdf, as 6 mutations e os
 * handlers (copiar link, baixar DOCX, arquivar). Consumido por
 * EstatutoDadosTab (conteúdo rico) e EstatutoAcoesSlot (botões de ação).
 */
export const useEstatutoCardActions = ({
  isOpen,
  estatutoId,
  onClose,
  canFinalizar = false,
  onFinalizar,
  finalizando = false,
}: UseEstatutoCardActionsParams) => {
  const queryClient = useQueryClient();
  const {
    getEstatuto,
    marcarLinkEnviado,
    validarEstatuto,
    syncAutentique,
    downloadDocumento,
    editarEstatuto,
  } = useEstatuto();
  const { arquivarCard, updateCard } = useKanban();
  const dialog = useDialog();

  const [linkCopied, setLinkCopied] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  // "Aguardando cliente" (T6.1): estado local do checkbox + motivo, sincronizado com o
  // estatuto carregado. Salvar via updateCard no card vinculado.
  const [aguardando, setAguardando] = useState(false);
  const [motivo, setMotivo] = useState('');

  const {
    isOpen: isAssinaturaOpen,
    onOpen: openAssinatura,
    onClose: closeAssinatura,
  } = useDisclosure();
  const { isOpen: isPdfOpen, onOpen: openPdf, onClose: closePdf } = useDisclosure();

  const { data, isLoading } = useQuery({
    queryKey: ['estatuto', estatutoId],
    queryFn: () => getEstatuto(estatutoId as number),
    // Fiel ao EstatutoDrawer original: só busca com o modal ABERTO (não em background).
    enabled: isOpen && !!estatutoId,
  });

  const estatuto = data?.data;

  // Sincroniza o checkbox/motivo com o estado atual do estatuto (T6.1) ao (re)carregar.
  useEffect(() => {
    setAguardando(!!estatuto?.aguardando_cliente);
    setMotivo(estatuto?.motivo_bloqueio ?? '');
  }, [estatuto?.aguardando_cliente, estatuto?.motivo_bloqueio]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['estatutos'] });
    queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
    if (estatutoId) queryClient.invalidateQueries({ queryKey: ['estatuto', estatutoId] });
  };

  // T6.1: salva "aguardando cliente" + motivo no card vinculado. O motivo é OPCIONAL
  // (contexto extra ao cliente). Desmarcar limpa o motivo; marcar sem motivo envia "".
  const aguardandoMutation = useMutation({
    mutationFn: () =>
      updateCard({
        id: estatuto!.kanban_card_id as number,
        aguardando_cliente: aguardando,
        motivo_bloqueio: aguardando ? motivo.trim() : null,
      }),
    onSuccess: () => {
      toast.success(
        aguardando ? 'Marcado como aguardando cliente' : 'Desmarcado — cliente liberado',
      );
      invalidate();
    },
    onError: () => toast.error('Erro ao atualizar "Aguardando cliente"'),
  });

  const aguardandoSemMudanca =
    aguardando === !!estatuto?.aguardando_cliente &&
    motivo.trim() === (estatuto?.motivo_bloqueio ?? '').trim();

  // Edição dos dados do processo (#14): PATCH parcial (título/cartório/advogado/empresa/
  // responsáveis). Os campos editáveis vêm da UI (EstatutoDadosTab controla o estado).
  const editarMutation = useMutation({
    mutationFn: (dados: IEditarEstatutoData) => editarEstatuto(estatutoId as number, dados),
    onSuccess: () => {
      toast.success('Dados do processo atualizados');
      invalidate();
    },
    onError: () => toast.error('Erro ao salvar os dados do processo'),
  });

  const marcarMutation = useMutation({
    mutationFn: () => marcarLinkEnviado(estatutoId as number),
    onSuccess: res => {
      toast.success(res?.message || 'Link marcado como enviado');
      invalidate();
    },
    onError: () => toast.error('Erro ao marcar link como enviado'),
  });

  const validarMutation = useMutation({
    mutationFn: () => validarEstatuto(estatutoId as number),
    onSuccess: res => {
      // O /validar agora dispara o envio automático pra Autentique. A message
      // varia: validado / validado e enviado / validado mas corrija o e-mail.
      // Surfacia o texto do backend pro admin saber se o envio saiu.
      const msg = res?.message || 'Estatuto validado';
      const enviado = /enviad/i.test(msg);
      const precisaCorrigir = /corrij|inválid|invalid|não foi feito|nao foi feito/i.test(msg);
      if (precisaCorrigir) {
        toast.warning(msg);
      } else if (enviado) {
        toast.success(msg);
      } else {
        toast.info(msg);
      }
      invalidate();
    },
    onError: () => toast.error('Erro ao validar estatuto'),
  });

  const syncMutation = useMutation({
    mutationFn: () => syncAutentique(estatutoId as number),
    onSuccess: res => {
      const novoStatus = res.data.status;
      if (novoStatus === 'assinado') {
        toast.success('Assinatura concluída!');
      } else if (novoStatus === 'rejeitado') {
        toast.error('Assinatura rejeitada — verifique na Autentique');
      } else {
        toast.info('Aguardando assinatura');
      }
      invalidate();
    },
    onError: (err: any) => {
      if (err?.response?.status === 404) {
        toast.error('Nenhum documento na Autentique ainda. Envie para assinatura primeiro.');
      } else {
        toast.error('Erro ao verificar assinatura');
      }
    },
  });

  const arquivarMutation = useMutation({
    mutationFn: (cardId: number) => arquivarCard(cardId),
    onSuccess: () => {
      toast.success('Estatuto arquivado');
      queryClient.invalidateQueries({ queryKey: ['estatutos'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
      queryClient.invalidateQueries({ queryKey: ['estatuto-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['estatuto-arquivados'] });
      onClose();
    },
    onError: () => toast.error('Erro ao arquivar estatuto'),
  });

  const handleArquivar = () => {
    if (!estatuto?.kanban_card_id) return;
    const cardId = estatuto.kanban_card_id;
    dialog({
      title: 'Arquivar estatuto?',
      status: 'warning',
      description:
        'O estatuto sai do quadro mas é preservado e pode ser restaurado na aba "Arquivados". Diferente de excluir.',
      confirmLabel: 'Arquivar',
      cancelLabel: 'Cancelar',
      onConfirm: () => arquivarMutation.mutate(cardId),
    });
  };

  const publicUrl = estatuto ? buildEstatutoPublicUrl(estatuto.hashed_link) : '';
  const acoes = estatuto ? acoesDoEstatuto(estatuto) : [];
  const dados = estatuto?.dados_preenchidos;
  const statusColor = estatuto ? STATUS_COLORS[estatuto.status] : null;

  const handleCopyLink = async () => {
    if (!publicUrl) return;
    const ok = await copyToClipboard(publicUrl);
    if (ok) {
      setLinkCopied(true);
      toast.success('Link copiado');
      setTimeout(() => setLinkCopied(false), 2000);
    } else {
      toast.error('Não foi possível copiar');
    }
  };

  const handleDownloadDocx = async () => {
    if (!estatuto) return;
    setDownloadingDocx(true);
    try {
      const blob = await downloadDocumento(estatuto.id, 'docx');
      const filename = (estatuto.titulo || `estatuto-${estatuto.id}`)
        .replace(/[^a-zA-Z0-9-_]+/g, '_')
        .slice(0, 80);
      // Constituição completa: os 5 .docx vêm num zip.
      downloadBlob(blob, `${filename}.${blob.type.includes('zip') ? 'zip' : 'docx'}`);
    } catch {
      toast.error('Erro ao baixar DOCX');
    } finally {
      setDownloadingDocx(false);
    }
  };

  return {
    estatuto,
    isLoading,
    acoes,
    dados,
    publicUrl,
    statusColor,
    linkCopied,
    downloadingDocx,
    aguardando,
    setAguardando,
    motivo,
    setMotivo,
    aguardandoSemMudanca,
    isAssinaturaOpen,
    openAssinatura,
    closeAssinatura,
    isPdfOpen,
    openPdf,
    closePdf,
    marcarMutation,
    validarMutation,
    syncMutation,
    arquivarMutation,
    aguardandoMutation,
    editarMutation,
    handleCopyLink,
    handleDownloadDocx,
    handleArquivar,
    // Gating de finalização, propagado do modal genérico:
    canFinalizar,
    onFinalizar,
    finalizando,
  };
};

export type EstatutoCardActions = ReturnType<typeof useEstatutoCardActions>;
